import React, { type JSX, type ReactNode } from "react";
import { ShieldAlert } from "lucide-react";
import type { AdminRecognitionAuditDetail } from "../../contracts/admin-read-models";
import { formatAdminDate } from "../../shared/formatting/format-admin-date";
import { AdminLocalizedLink } from "../../shared/locale/admin-dashboard-localized-link";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import { AdminDetailPanel } from "../../shared/ui/admin-detail-panel";
import { AdminStatusBadge } from "../../shared/ui/admin-status-badge";
import type { RecognitionAuditDetailViewModel, RecognitionAuditRawActionViewModel } from "./recognition-audit-view-model";
import type { RecognitionRawArtifact } from "./recognition-raw-artifact-viewer";

type RecognitionAuditDetailPanelProps = {
  detail: AdminRecognitionAuditDetail;
  locale: AdminDashboardLocale;
  onRequestRawAccess: (artifact: RecognitionRawArtifact) => void;
  viewModel: RecognitionAuditDetailViewModel;
};

type RawActionButtonModel = {
  action: RecognitionAuditRawActionViewModel;
  artifact: RecognitionRawArtifact;
  label: string;
};

function blockList(items: ReactNode[]): JSX.Element {
  return (
    <div style={{ display: "grid", gap: "10px" }}>
      {items.map((item, index) => (
        <div
          key={index}
          style={{
            padding: "10px 12px",
            borderRadius: "12px",
            border: "1px solid rgba(148, 163, 184, 0.22)",
            background: "rgba(248, 250, 252, 0.9)",
            lineHeight: 1.5,
          }}
        >
          {item}
        </div>
      ))}
    </div>
  );
}

function formatBytes(value: number): string {
  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  }

  if (value >= 1024) {
    return `${(value / 1024).toFixed(1)} KB`;
  }

  return `${value} B`;
}

function formatTokens(value: number | undefined): string {
  return new Intl.NumberFormat("en-US").format(value ?? 0);
}

function formatUsd(value: number | undefined): string {
  return `$${(value ?? 0).toFixed(4)}`;
}

function buildRawActionButtons(
  locale: AdminDashboardLocale,
  actions: RecognitionAuditDetailViewModel["rawActions"],
  sources: AdminRecognitionAuditDetail["sources"],
  onRequestRawAccess: (artifact: RecognitionRawArtifact) => void,
): JSX.Element | null {
  const requestLabel = pickAdminDashboardLocaleText(locale, { en: "Request", ko: "요청" });
  const sourceFileActions = actions.sourceFiles.length > 0
    ? actions.sourceFiles
    : [actions.sourceFile];
  const availableActions: RawActionButtonModel[] = [
    ...sourceFileActions.map((action, index) => ({
      action,
      label: `${requestLabel} ${action.targetKind}${sourceFileActions.length > 1 ? ` ${index + 1}` : ""}`,
      artifact: {
        action,
        id: `source_${index}`,
        kind: "source" as const,
        label: sources[index]?.fileName ?? action.targetKind,
        mimeType: sources[index]?.mimeType,
      },
    })),
    {
      action: actions.extractedText,
      label: `${requestLabel} ${actions.extractedText.targetKind}`,
      artifact: { action: actions.extractedText, id: "extracted-text", kind: "text" as const, label: actions.extractedText.targetKind },
    },
    {
      action: actions.geminiResponse,
      label: `${requestLabel} ${actions.geminiResponse.targetKind}`,
      artifact: { action: actions.geminiResponse, id: "gemini", kind: "json" as const, label: actions.geminiResponse.targetKind },
    },
  ].filter((button) => button.action.state !== "hidden");

  if (availableActions.length === 0) {
    return null;
  }

  return (
    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
      {availableActions.map(({ action, artifact, label }) => (
        <button
          key={`${action.targetKind}_${action.targetId ?? "none"}`}
          onClick={() => onRequestRawAccess(artifact)}
          disabled={action.state === "disabled"}
          title={action.targetId}
          style={{
            minHeight: "38px",
            padding: "0 12px",
            borderRadius: "12px",
            border: "1px solid rgba(148, 163, 184, 0.35)",
            background: action.state === "disabled" ? "#f8fafc" : "#fff7ed",
            color: action.state === "disabled" ? "#94a3b8" : "#9a3412",
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
          }}
          type="button"
        >
          <ShieldAlert size={16} />
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
}

export function RecognitionAuditDetailPanel(
  props: RecognitionAuditDetailPanelProps,
): JSX.Element {
  const copy = {
    title: pickAdminDashboardLocaleText(props.locale, { en: "Recognition audit detail", ko: "인식 감사 상세" }),
    startedAt: pickAdminDashboardLocaleText(props.locale, { en: "Started", ko: "시작" }),
    completedAt: pickAdminDashboardLocaleText(props.locale, { en: "Completed", ko: "완료" }),
    outcome: pickAdminDashboardLocaleText(props.locale, { en: "Outcome", ko: "결과" }),
    failedStage: pickAdminDashboardLocaleText(props.locale, { en: "Failed stage", ko: "실패 위치" }),
    errorCode: pickAdminDashboardLocaleText(props.locale, { en: "Error code", ko: "에러 코드" }),
    sourceFiles: pickAdminDashboardLocaleText(props.locale, { en: "Source files", ko: "원본 파일" }),
    ocrPreview: pickAdminDashboardLocaleText(props.locale, { en: "Preview", ko: "미리보기" }),
    engine: pickAdminDashboardLocaleText(props.locale, { en: "Engine", ko: "엔진" }),
    fieldCount: pickAdminDashboardLocaleText(props.locale, { en: "Field count", ko: "필드 수" }),
    aiUsage: pickAdminDashboardLocaleText(props.locale, { en: "AI usage", ko: "AI 사용량" }),
    diff: pickAdminDashboardLocaleText(props.locale, { en: "Diff summary", ko: "Diff 요약" }),
    related: pickAdminDashboardLocaleText(props.locale, { en: "Related links", ko: "관련 링크" }),
    privacy: pickAdminDashboardLocaleText(props.locale, { en: "Privacy", ko: "개인정보" }),
    rawHistory: pickAdminDashboardLocaleText(props.locale, { en: "Raw access history", ko: "Raw 접근 이력" }),
    containsPersonalData: pickAdminDashboardLocaleText(props.locale, { en: "Contains personal data", ko: "개인정보 포함" }),
    retentionPolicy: pickAdminDashboardLocaleText(props.locale, { en: "Retention policy", ko: "보관 정책" }),
    consentStatus: pickAdminDashboardLocaleText(props.locale, { en: "Consent", ko: "동의 상태" }),
    exportCandidate: pickAdminDashboardLocaleText(props.locale, { en: "Export candidate", ko: "Export 후보" }),
    deletionStatus: pickAdminDashboardLocaleText(props.locale, { en: "Deletion status", ko: "삭제 상태" }),
    quarantineStatus: pickAdminDashboardLocaleText(props.locale, { en: "Quarantine status", ko: "격리 상태" }),
    expiresAt: pickAdminDashboardLocaleText(props.locale, { en: "Expires", ko: "만료" }),
  };

  return (
    <AdminDetailPanel
      title={copy.title}
      headerAction={<AdminStatusBadge value={props.detail.lifecycle.status} />}
      footer={buildRawActionButtons(props.locale, props.viewModel.rawActions, props.detail.sources, props.onRequestRawAccess)}
      sections={[
        {
          title: props.viewModel.sections[0]?.title ?? "Lifecycle",
          items: [
            { key: "startedAt", label: copy.startedAt, value: formatAdminDate(props.detail.lifecycle.startedAt) },
            { key: "completedAt", label: copy.completedAt, value: props.detail.lifecycle.completedAt ? formatAdminDate(props.detail.lifecycle.completedAt) : "-" },
            { key: "outcome", label: copy.outcome, value: props.detail.lifecycle.outcome },
            { key: "failedStage", label: copy.failedStage, value: props.detail.lifecycle.failedStage ?? "-" },
            { key: "errorCode", label: copy.errorCode, value: props.detail.lifecycle.errorCode ?? "-" },
          ],
        },
        {
          title: props.viewModel.sections[1]?.title ?? copy.sourceFiles,
          items: [
            {
              key: "sources",
              label: copy.sourceFiles,
              value: blockList(
                props.detail.sources.map(
                  (source) => <><span>{`${source.fileName} | ${source.mimeType} | ${formatBytes(source.byteSize)} | ${source.pageCount}p / ${source.imageCount}img | sha ${source.sha256Prefix} | ${source.availabilityStatus}`}</span>{source.previewUrl && source.mimeType.startsWith("image/") ? <img alt={source.fileName} src={source.previewUrl} style={{ display: "block", marginTop: "10px", maxWidth: "100%", borderRadius: "10px" }} /> : null}</>,
                ),
              ),
            },
          ],
        },
        {
          title: props.viewModel.sections[2]?.title ?? "Extracted text",
          items: [
            { key: "engine", label: copy.engine, value: props.detail.textExtraction.engine },
            { key: "fieldCount", label: copy.fieldCount, value: String(props.detail.textExtraction.fieldCount) },
            {
              key: "preview",
              label: copy.ocrPreview,
              value: props.detail.textExtraction.extractedTextPreview.slice(0, 500),
            },
          ],
        },
        {
          title: props.viewModel.sections[3]?.title ?? copy.aiUsage,
          items: [
            {
              key: "usage",
              label: copy.aiUsage,
              value:
                props.detail.aiUsage.length > 0
                  ? blockList(
                      props.detail.aiUsage.map(
                        (usage) =>
                          `${usage.provider} / ${usage.model} / ${usage.usageMetadata} / ${formatTokens(usage.totalTokens)} tokens / ${formatUsd(usage.estimatedCostUsd)} / ${usage.pricingVersion}`,
                      ),
                    )
                  : "-",
            },
          ],
        },
        {
          title: props.viewModel.sections[4]?.title ?? copy.diff,
          items: [
            {
              key: "draftDiff",
              label: copy.diff,
              value: `${props.detail.draftAndFinal.changedFieldCount} changed | ${props.detail.draftAndFinal.recognizedDraftSummary} | ${props.detail.draftAndFinal.userFinalSummary}`,
            },
          ],
        },
        {
          title: props.viewModel.sections[5]?.title ?? copy.related,
          items: [
            {
              key: "related",
              label: copy.related,
              value: (
                <div style={{ display: "grid", gap: "10px" }}>
                  {props.viewModel.relatedLinks.aiJob ? (
                    <div style={{ padding: "10px 12px", borderRadius: "12px", border: "1px solid rgba(148, 163, 184, 0.22)", background: "rgba(248, 250, 252, 0.9)" }}>
                      AI job:{" "}
                      {props.viewModel.relatedLinks.aiJob.to ? (
                        <AdminLocalizedLink to={props.viewModel.relatedLinks.aiJob.to}>
                          {props.viewModel.relatedLinks.aiJob.label}
                        </AdminLocalizedLink>
                      ) : (
                        props.viewModel.relatedLinks.aiJob.label
                      )}
                    </div>
                  ) : null}
                  {props.viewModel.relatedLinks.draftRecord ? (
                    <div style={{ padding: "10px 12px", borderRadius: "12px", border: "1px solid rgba(148, 163, 184, 0.22)", background: "rgba(248, 250, 252, 0.9)" }}>
                      Draft record: {props.viewModel.relatedLinks.draftRecord.label}
                    </div>
                  ) : null}
                  {props.viewModel.relatedLinks.finalRecord ? (
                    <div style={{ padding: "10px 12px", borderRadius: "12px", border: "1px solid rgba(148, 163, 184, 0.22)", background: "rgba(248, 250, 252, 0.9)" }}>
                      Final record: {props.viewModel.relatedLinks.finalRecord.label}
                    </div>
                  ) : null}
                </div>
              ),
            },
          ],
        },
        {
          title: props.viewModel.sections[6]?.title ?? copy.privacy,
          items: [
            { key: "containsPersonalData", label: copy.containsPersonalData, value: props.detail.privacy.containsPersonalData ? "yes" : "no" },
            { key: "consentStatus", label: copy.consentStatus, value: props.viewModel.privacy.consentStatusLabel },
            { key: "retentionPolicy", label: copy.retentionPolicy, value: props.detail.privacy.retentionPolicyVersion },
            { key: "exportCandidate", label: copy.exportCandidate, value: props.viewModel.privacy.exportCandidateEligibleLabel },
            { key: "deletionStatus", label: copy.deletionStatus, value: props.viewModel.privacy.deletionStatusLabel },
            { key: "quarantineStatus", label: copy.quarantineStatus, value: props.viewModel.privacy.quarantineStatusLabel },
          ],
        },
        {
          title: props.viewModel.sections[7]?.title ?? copy.rawHistory,
          items: [
            {
              key: "rawHistory",
              label: copy.rawHistory,
              value:
                props.detail.rawAccessHistory.length > 0
                  ? blockList(
                      props.detail.rawAccessHistory.map(
                        (history) =>
                          `${history.actorDisplayName} | ${history.purpose} | ${history.targetKind} | ${formatAdminDate(history.accessedAt)} | ${history.expiresAt ? `${copy.expiresAt} ${formatAdminDate(history.expiresAt)} | ` : ""}${history.grantStatus}`,
                      ),
                    )
                  : "-",
            },
          ],
        },
      ]}
    />
  );
}
